// ⚠️ 本注册表只用于文档 + dev warn，运行时不做参数白名单校验 —— 所以「声明了但实现从不产出」
// 的字段不会报错、只会静默缺席。2026-09-23 全链路 review（doc/review/埋点事件与参数全链路Review报告_2026-09-23.md §2 R-9）
// 清理了 5 条这类误导性声明，判定标准：该字段在实现里**只能取到常量**（无区分信息），
// 且事件本身已有真实区分维度时，从注册表删除；能取到真实值的则补实现，不删。
//   · IMAGE_UPLOAD.source_position（调用方只有 method，无区域信息）
//   · UPLOAD_STARTED.event_label / UPLOAD_FAILED.event_label（只会是常量，真实维度是 upload_step / method / reason_code / error_name）
//   · UPLOAD_RESULT_SHOWCASE_REVEALED.element_type（真实维度是 element_name + method）
//   · UPLOAD_JS_ERROR_CAPTURED.source_position（真实维度是 upload_step + error_type）
export const UPLOAD_EVENTS = Object.freeze({
  IMAGE_UPLOAD: {
    name: 'image_upload',
    category: 'Upload',
    description: '图片上传并完成本地准备后触发；沿用历史事件名',
    requiredParams: ['content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'event_label', 'mime_type', 'file_size_bucket', 'original_size_bucket', 'compressed', 'is_reupload', 'module_name', 'method']
  },
  UPLOAD_STARTED: {
    name: 'upload_started',
    category: 'Upload',
    description: '用户开始一次上传尝试时触发',
    requiredParams: [],
    optionalParams: ['is_logged_in', 'upload_step', 'module_name', 'source_position', 'method', 'original_size_bucket']
  },
  UPLOAD_FAILED: {
    name: 'upload_failed',
    category: 'Upload',
    description: '上传文件校验、读取或处理失败时触发',
    requiredParams: ['reason_code'],
    // `drop_payload_type`（2026-10-16 新增）：`non_file_dropped` 有两个成因共用一个 reason_code
    //   · item.kind==='string'（真·拖入 URL/文本）→ text_uri_list / text_html / text_plain / other_string
    //   · dataTransfer.files 为空（兜底）          → no_entry_api（浏览器无 webkitGetAsEntry）/ no_files_fallback
    // 起因：BQ 实测这条 reason_code 无法区分两者，`item.type` 只进了 console trace（见 useImageFileIngress）。
    // ⚠️ 只上报**归一化枚举**，绝不上报 `dataTransfer.types` 原值 —— 其内容可能含 URL（字典 §7 禁字段）。
    // `file_ext`（2026-10-17 新增）：`mime_type` 依赖浏览器 `File.type`，无扩展名/罕见后缀的文件该值是**空串**，
    //   而空串会被 analyticsPayload 当空值丢弃 ⇒「不知道格式」与「没传」不可区分（dev 实测 11020B 文件即如此）。
    //   ⇒ 同时引入两个口径：`mime_type='unknown'`（有文件但浏览器没给类型）+ `file_ext`（归一化后缀 / `no_ext`）。
    //   ⚠️ 只上报扩展名，不上报文件名。
    optionalParams: ['is_logged_in', 'mime_type', 'file_size_bucket', 'http_status', 'module_name', 'source_position', 'method', 'original_size_bucket', 'error_name', 'drop_payload_type', 'file_ext']
  },
  // ── 提示展示（2026-10-16 新增）──
  // 起因：一次线上取证发现 `upload_failed` 有上报，但**「用户是否真的看到了提示」完全无观测点**：
  //   `ImageUpload.vue` 的 `showError()` 只置 `showDialog/dialogHeader/dialogMessage`，全文件 ~30 处
  //   `uploadAnalytics.*` 里没有一条对应它；而本项目对「用户可见界面出现」有明确约定
  //   （`billing_notice_viewed` / `feature_gate_shown` / `upload_cta_button_shown` / `upload_result_showcase_revealed`）。
  // 与 `upload_failed` 的关系：**不是 1:1** ——
  //   · `file_reader_aborted` / `empty_file` 走 onReject 但（改造前）无弹框 ⇒ 有 failed 无 notice；
  //   · `preset_required` / `invalid_preset` 等 preset 校验路径有弹框但归 `upload_start_analysis_blocked`。
  //   故单列一个事件，`reason_code` 取**同一枚举**，便于失败→提示配对与「提示是否被吞」定位。
  UPLOAD_ERROR_NOTICE_SHOWN: {
    name: 'upload_error_notice_shown',
    category: 'Upload',
    description: 'Upload 页面向用户展示错误提示弹窗（showError：文件校验/读取失败、preset 校验失败、草稿恢复失败等）时触发；与同 reason_code 的上游失败事件配对，用于判定"失败已上报但提示未真正呈现"',
    requiredParams: ['reason_code'],
    optionalParams: ['is_logged_in', 'upload_step', 'method', 'module_name', 'source_position']
  },
  UPLOAD_PAGE_VIEWED: {
    name: 'upload_page_viewed',
    category: 'Upload',
    description: 'Upload 页面进入时触发',
    requiredParams: [],
    optionalParams: ['is_logged_in', 'has_resume_query', 'has_history_reentry_query', 'has_preset_error_query', 'module_name', 'source_position', 'navigation_trigger']
  },
  UPLOAD_BOOTSTRAP_COMPLETED: {
    name: 'upload_bootstrap_completed',
    category: 'Upload',
    description: 'Upload 页面运行时依赖初始化完成时触发',
    requiredParams: ['action_status'],
    optionalParams: ['is_logged_in', 'catalog_total', 'fallback_used', 'reason_code']
  },
  UPLOAD_BOOTSTRAP_FAILED: {
    name: 'upload_bootstrap_failed',
    category: 'Upload',
    description: 'Upload 页面运行时依赖初始化失败时触发',
    requiredParams: ['reason_code'],
    optionalParams: ['is_logged_in', 'fallback_used', 'http_status']
  },
  UPLOAD_PRESET_SELECTED: {
    name: 'upload_preset_selected',
    category: 'Upload',
    description: '用户选择 reference preset 时触发',
    requiredParams: ['content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'reference_code', 'preset_category', 'upload_step']
  },
  UPLOAD_START_ANALYSIS_CLICKED: {
    name: 'upload_start_analysis_clicked',
    category: 'Upload',
    description: '用户点击启动分析按钮时触发',
    requiredParams: ['content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'has_preset', 'upload_step', 'related_content_id', 'related_content_type']
  },
  UPLOAD_START_ANALYSIS_BLOCKED: {
    name: 'upload_start_analysis_blocked',
    category: 'Upload',
    description: '启动分析被前端状态、preset 校验或前置判定阻止时触发',
    requiredParams: ['reason_code'],
    optionalParams: ['is_logged_in', 'upload_step', 'action_status', 'request_id', 'http_status', 'content_id', 'content_type', 'related_content_id', 'related_content_type']
  },
  UPLOAD_LOGIN_REDIRECTED: {
    name: 'upload_login_redirected',
    category: 'Upload',
    description: 'Upload 启动分析过程中因未登录或认证检查失败跳转登录时触发',
    requiredParams: ['reason_code'],
    optionalParams: ['is_logged_in', 'redirect_target', 'content_id', 'content_type', 'related_content_id', 'related_content_type']
  },
  UPLOAD_LOGIN_DRAFT_RESTORED: {
    name: 'upload_login_draft_restored',
    category: 'Upload',
    description: '登录回流后恢复 Upload 草稿时触发',
    requiredParams: ['action_status'],
    optionalParams: ['is_logged_in', 'reason_code', 'content_id', 'content_type', 'related_content_id', 'related_content_type']
  },
  UPLOAD_PREFLIGHT_COMPLETED: {
    name: 'upload_preflight_completed',
    category: 'Upload',
    description: 'Upload 启动分析前置报价/准入判定返回时触发',
    requiredParams: ['action_status'],
    optionalParams: ['is_logged_in', 'service', 'operation_type', 'request_id', 'reason_code', 'http_status', 'content_id', 'content_type', 'related_content_id', 'related_content_type']
  },
  UPLOAD_RESULT_NAVIGATION: {
    name: 'upload_result_navigation',
    category: 'Upload',
    description: 'Upload 提交后跳转 Result 的结果',
    requiredParams: ['action_status'],
    optionalParams: ['is_logged_in', 'reason_code', 'content_id', 'content_type', 'related_content_id', 'related_content_type']
  },
  UPLOAD_REFERENCE_IMAGE_FAILED: {
    name: 'upload_reference_image_failed',
    category: 'Upload',
    description: 'reference 图片展示、缓存或预热失败时触发',
    requiredParams: ['reason_code'],
    optionalParams: ['is_logged_in', 'reference_code', 'content_id', 'content_type']
  },
  UPLOAD_FILE_DIALOG_OPENED: {
    name: 'upload_file_dialog_opened',
    category: 'Upload',
    description: '用户点击上传入口、打开系统文件选择框时触发',
    requiredParams: [],
    optionalParams: ['is_logged_in', 'upload_step', 'module_name', 'source_position', 'method']
  },
  UPLOAD_CASE_STUDY_CLICKED: {
    name: 'upload_case_study_clicked',
    category: 'Upload',
    description: 'Upload 页对比展示区「查看完整案例」CTA 点击（新标签打开公开只读案例）时触发',
    requiredParams: [],
    optionalParams: ['is_logged_in', 'source_position', 'element_name', 'element_type', 'cta_type', 'destination_type', 'destination_id', 'content_id', 'content_type', 'action_status', 'duration_ms', 'event_label', 'method']
  },
  UPLOAD_RESULT_SHOWCASE_REVEALED: {
    name: 'upload_result_showcase_revealed',
    category: 'Upload',
    description: 'Upload 页对比展示区内容（gap 标注 / AI 分析预览）首次 hover 曝光时触发',
    requiredParams: ['content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'source_position', 'element_name', 'event_label', 'method']
  },
  // ── 上传流程诊断事件（2026-08-21 新增）：补齐「上传成功 → 跳登录页」链路的可观测性盲区 ──
  UPLOAD_WORKSPACE_REVEALED: {
    name: 'upload_workspace_revealed',
    category: 'Upload',
    description: '上传成功后 workspace 态（原图预览 + CTA 操作区）渲染完成时触发；用于确认上传成功 UI 确已呈现，区分"传完即走"与"UI 未渲染"',
    requiredParams: ['upload_step'],
    optionalParams: ['is_logged_in', 'content_id', 'content_type', 'module_name', 'source_position']
  },
  UPLOAD_CTA_BUTTON_SHOWN: {
    name: 'upload_cta_button_shown',
    category: 'Upload',
    description: 'workspace 态下「Start Analysis」/「Skip Preset」CTA 按钮曝光时触发（按钮可见且可交互），用于确认用户看得到下一步入口',
    requiredParams: ['button_name', 'is_enabled'],
    optionalParams: ['is_logged_in', 'upload_step', 'content_id', 'content_type', 'module_name', 'source_position']
  },
  UPLOAD_CTA_BUTTON_CLICKED: {
    name: 'upload_cta_button_clicked',
    category: 'Upload',
    description: '用户点击 CTA 按钮的瞬间触发（先于 goToResultWithPreset 业务逻辑）；与 upload_start_analysis_clicked 对比可区分"未点击"与"点击后逻辑失败"',
    requiredParams: ['button_name'],
    optionalParams: ['is_logged_in', 'upload_step', 'content_id', 'content_type', 'module_name', 'source_position', 'method']
  },
  UPLOAD_LOGIN_REDIRECT_FAILED: {
    name: 'upload_login_redirect_failed',
    category: 'Upload',
    description: '匿名用户触发登录跳转但 router.push(/login) 失败（NavigationFailure / reject）时触发；用于捕获"判定要跳登录但未到达登录页"的场景',
    requiredParams: ['reason_code'],
    optionalParams: ['is_logged_in', 'redirect_target', 'content_id', 'content_type', 'related_content_id', 'related_content_type', 'upload_step']
  },
  UPLOAD_PAGE_EXITED: {
    name: 'upload_page_exited',
    category: 'Upload',
    description: 'upload 页在 workspace 态被关闭/隐藏/路由切换离开时触发（beforeunload / visibilitychange=hidden / 路由 beforeEach）；用于判断用户是主动离开还是流程被阻断',
    requiredParams: ['exit_trigger', 'upload_step'],
    optionalParams: ['is_logged_in', 'content_id', 'content_type', 'module_name', 'source_position', 'time_on_page_ms', 'had_started_analysis']
  },
  UPLOAD_JS_ERROR_CAPTURED: {
    name: 'upload_js_error_captured',
    category: 'Upload',
    description: 'upload 组件生命周期内捕获到的未处理 JS 错误（window.error / unhandledrejection）时触发；用于发现阻断流程的运行时异常',
    requiredParams: ['error_type', 'error_message'],
    optionalParams: ['is_logged_in', 'upload_step', 'content_id', 'content_type', 'module_name', 'stack_trace']
  },
  UPLOAD_IMAGE_PREVIEW_LOADED: {
    name: 'upload_image_preview_loaded',
    category: 'Upload',
    description: '上传成功后原图预览 <img> onload 完成时触发；用于排除"预览白屏导致用户不敢点下一步"',
    requiredParams: ['content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'upload_step', 'module_name', 'source_position', 'load_duration_ms']
  },
  UPLOAD_IMAGE_PREVIEW_FAILED: {
    name: 'upload_image_preview_failed',
    category: 'Upload',
    description: '上传成功后原图预览 <img> onerror 失败时触发',
    requiredParams: ['reason_code', 'content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'upload_step', 'module_name', 'source_position', 'http_status']
  },
  // ── Reference 浏览交互埋点（2026-08-29 新增）：补齐分类切换 / 缩略图滚动 / 缩略图点击 / 原图预览点击放大 的可观测性 ──
  UPLOAD_REFERENCE_CATEGORY_CHANGED: {
    name: 'upload_reference_category_changed',
    category: 'Upload',
    description: '用户在 workspace 态切换 reference 分类（侧边栏 / PresetSelectorPanel 分类导航）时触发',
    requiredParams: ['preset_category'],
    optionalParams: ['is_logged_in', 'previous_category', 'module_name', 'source_position', 'upload_step']
  },
  UPLOAD_REFERENCE_THUMBNAILS_SCROLLED: {
    name: 'upload_reference_thumbnails_scrolled',
    category: 'Upload',
    description: '用户在 workspace 态滚动 reference 缩略图区域时触发（节流上报，用于判断用户是否浏览过更多参考图）',
    requiredParams: [],
    optionalParams: ['is_logged_in', 'scroll_percentage', 'reached_bottom', 'preset_category', 'module_name', 'source_position', 'upload_step']
  },
  UPLOAD_REFERENCE_THUMBNAIL_CLICKED: {
    name: 'upload_reference_thumbnail_clicked',
    category: 'Upload',
    description: '用户点击 reference 缩略图卡片（意图选中/查看大图）时触发，先于 selectPresetStyle 的选中逻辑，区分"未点击"与"点击后选中被阻断"',
    requiredParams: ['content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'reference_code', 'preset_category', 'upload_step', 'module_name', 'source_position', 'method']
  },
  UPLOAD_PREVIEW_IMAGE_CLICKED: {
    name: 'upload_preview_image_clicked',
    category: 'Upload',
    description: '用户点击上传原图预览图（触发 PrimeVue Image 灯箱放大查看）时触发',
    requiredParams: ['content_id', 'content_type'],
    optionalParams: ['is_logged_in', 'upload_step', 'module_name', 'source_position', 'method']
  },
  // ── 移动端交互体验优化埋点（plan_upload页面移动端交互体验优化方案.md §四，2026-09-15 新增） ──
  UPLOAD_LIGHTBOX_OPENED: {
    name: 'upload_lightbox_opened',
    category: 'Upload',
    description: 'reference 缩略图灯箱（PrimeVue Image preview）打开时触发；lightbox_trigger 拆解桌面「点缩略图区域」与触屏「显式放大按钮」两个入口，用于量化 P0-1 的点击语义混淆是否消除',
    requiredParams: ['lightbox_trigger'],
    optionalParams: ['is_logged_in', 'upload_step', 'reference_code', 'preset_category', 'content_id', 'content_type', 'module_name', 'source_position']
  },
  UPLOAD_STICKY_CTA_SHOWN: {
    name: 'upload_sticky_cta_shown',
    category: 'Upload',
    description: '移动端吸底 CTA 条（.mobile-sticky-cta）进入 workspace 态渲染并可见时触发；与 upload_sticky_cta_clicked 组成曝光→点击漏斗，用于衡量 P0-2「主 CTA 需滚到底」是否消除',
    requiredParams: ['is_enabled'],
    optionalParams: ['is_logged_in', 'upload_step', 'has_preset', 'content_id', 'content_type', 'module_name', 'source_position']
  },
  UPLOAD_STICKY_CTA_CLICKED: {
    name: 'upload_sticky_cta_clicked',
    category: 'Upload',
    description: '用户点击移动端吸底 CTA 条「开始分析」时触发（先于业务逻辑）；同一动作同时上报 upload_cta_button_clicked{source_position=mobile_sticky_cta} 以保持跨设备 CTA 漏斗口径可比',
    requiredParams: [],
    optionalParams: ['is_logged_in', 'upload_step', 'has_preset', 'content_id', 'content_type', 'module_name', 'source_position']
  },
  UPLOAD_REFERENCE_GRID_RENDER: {
    name: 'upload_reference_grid_render',
    category: 'Upload',
    description: 'reference 网格渲染完成或尺寸/列表变化后触发，上报当前列数（columns）与首屏可见卡片数（visible_cards）；用于量化 P2-8「一屏只看得到 1 张参考图」的改善',
    requiredParams: ['columns'],
    optionalParams: ['is_logged_in', 'visible_cards', 'total_cards', 'preset_category', 'upload_step', 'module_name', 'source_position']
  }
});
